import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { isReady } from "@/data/books";

/**
 * BookCover — renders the real cover image once supplied (cover_asset),
 * otherwise a deliberately designed typographic cover in the series'
 * editorial style: deep canvas, oversized Telugu title, hairline frame.
 * With `tilt`, the cover responds to the cursor with a soft 3D rotation.
 */

const PALETTES = {
  1: { bg: "#0D1B2A", ink: "#FBF7EE", sub: "#FBF7EE", accent: "#F57F17", frame: "rgba(251,247,238,0.28)" },
  2: { bg: "#004D40", ink: "#FBF7EE", sub: "#FBF7EE", accent: "#F5B25C", frame: "rgba(251,247,238,0.28)" },
  3: { bg: "#FBF7EE", ink: "#0D1B2A", sub: "#0D1B2A", accent: "#D86B0F", frame: "rgba(13,27,42,0.3)" },
  4: { bg: "#F57F17", ink: "#0D1B2A", sub: "#0D1B2A", accent: "#FBF7EE", frame: "rgba(13,27,42,0.3)" },
  bundle: { bg: "#0D1B2A", ink: "#FBF7EE", sub: "#FBF7EE", accent: "#F57F17", frame: "rgba(251,247,238,0.28)" },
};

function TypographicCover({ book, palette }) {
  const telugu = book.title_telugu || "చిన్ని";
  return (
    <div
      className="relative w-full h-full flex flex-col justify-between p-[9%] overflow-hidden"
      style={{ backgroundColor: palette.bg }}
    >
      {/* oversized watermark glyph */}
      <span
        aria-hidden="true"
        className="font-telugu absolute -right-[8%] -bottom-[14%] text-[11rem] leading-none pointer-events-none select-none"
        style={{ color: palette.accent, opacity: 0.14 }}
      >
        {telugu.slice(0, 2)}
      </span>

      {/* hairline inset frame */}
      <span aria-hidden="true" className="absolute inset-[4.5%] border pointer-events-none" style={{ borderColor: palette.frame }} />

      <div className="relative flex items-start justify-between">
        <span
          className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-[0.3em]"
          style={{ color: palette.accent }}
        >
          Chinnu<br />Talking<br />Adventures
        </span>
        <span
          className="w-9 h-9 rounded-full border flex items-center justify-center font-serif-d text-[15px]"
          style={{ borderColor: palette.frame, color: palette.ink }}
        >
          {book.book_number || "★"}
        </span>
      </div>

      <div className="relative">
        <div
          className="font-telugu leading-[1.15] text-[clamp(30px,4.6vw,44px)]"
          style={{ color: palette.accent }}
        >
          {telugu}
        </div>
        <div
          className="mt-[6%] font-serif-d leading-[1.05] text-[clamp(24px,3.4vw,36px)]"
          style={{ color: palette.ink }}
        >
          {isReady(book.title) ? book.title : "Chinnu"}
        </div>
      </div>

      <div className="relative flex items-center justify-between">
        <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.26em] font-semibold" style={{ color: palette.sub, opacity: 0.75 }}>
          LifeIQ Books
        </span>
        <MessageCircle size={15} style={{ color: palette.accent }} />
      </div>
    </div>
  );
}

export default function BookCover({ book, tilt = false, className = "", shadow = "book-shadow-soft", testId }) {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 180, damping: 20 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-9, 9]), { stiffness: 180, damping: 20 });

  const onMove = (e) => {
    if (!tilt || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => { mx.set(0.5); my.set(0.5); };

  const palette = PALETTES[book.book_number] || PALETTES.bundle;
  const hasRealCover = isReady(book.cover_asset);

  const inner = hasRealCover ? (
    <img
      src={book.cover_asset}
      alt={isReady(book.title) ? `${book.title} — cover` : "Chinnu book cover"}
      className="w-full h-full object-cover"
    />
  ) : (
    <TypographicCover book={book} palette={palette} />
  );

  if (!tilt) {
    return (
      <div data-testid={testId} className={`aspect-[3/4] rounded-[3px] overflow-hidden ${shadow} ${className}`}>
        {inner}
      </div>
    );
  }

  return (
    <div style={{ perspective: 1100 }} className={className}>
      <motion.div
        ref={ref}
        data-testid={testId}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        style={{ rotateX, rotateY }}
        className={`cover-frame aspect-[3/4] rounded-[3px] overflow-hidden ${shadow}`}
      >
        {inner}
      </motion.div>
    </div>
  );
}
